import { DatePicker, Table, Tooltip } from "antd";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import WellBeEMPIService from "@services/empiService.service";

import "./fileTrackingReport.scss";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { CSVLink } from "react-csv";
import { formatDownloadFileNameDate } from "@utils/helpers";
import { sortBy } from "lodash";

const FileTrackingReport = () => {
  const pageName = "FILE-TRACKING-REPORT";

  const [filters, setFilters] = useState<any>({ date: moment().format("YYYY-MM-DD") });
  const [tableCOlumnConfig, setTableCOlumnConfig] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [tableSpinner, setTableSpinner] = useState(false);
  const [filterColumnOption, setFilterColumnOption] = useState<any>(null);
  const [tableFilters, setTableFilters] = useState<any>(null);
  const [tableSort, setTableSort] = useState<any>(null);
  const [fileAvailableDates, setFileAvailableDates] = useState<any>([]);

  useEffect(() => {
    getFileTrackingAvailableDates();
    getColumnConfig();
  }, []);

  const getFileTrackingAvailableDates = () => {
    WellBeEMPIService.getFileTrackingAvailableDates().subscribe({
      next: (v: any) => {
        setFileAvailableDates(v?.data?.data?.result || []);
      },
      error: (e) => {
        console.log(e);
      },
    });
  };

  const getColumnConfig = () => {
    setTableSpinner(true);
    WellBeEMPIService.getColumnsList({ pageName }).subscribe({
      next: (v) => {
        const colData = v.data.data.columns;
        if (colData && colData?.length) {
          fetchTableColumnFIlterOPtions(colData);
        } else {
          setTableSpinner(false);
        }
      },
      error: (e) => {
        console.log(e);
        setTableSpinner(false);
      },
    });
  };

  ///fetch-file-tracking
  const fetchTableData = useCallback(
    (filter = filters) => {
      setTableSpinner(true);
      WellBeEMPIService.getFileTrackingData(filter).subscribe({
        next: (response: any) => {
          setTableData(response.data.data.result);
          setTableFilters(
            tableFilters
              ? Object.keys(tableFilters)?.reduce((acc: any, key: any) => {
                  acc[key] = [];
                  return acc;
                }, {})
              : {}
          );
          setTableSort(null);
          setTableSpinner(false);
        },
        error: (error) => {
          setTableData([]);
          setTableSpinner(false);
        },
      });
    },
    [filters]
  );

  const fetchTableColumnFIlterOPtions = (ColConfigData: any) => {
    WellBeEMPIService.getFilterableColumnAndOptions().subscribe({
      next: (response: any) => {
        const filterOptions = response.data.data.result;
        setTableFilters(
          Object.keys(filterOptions)?.reduce((acc: any, key: any) => {
            acc[key] = [];
            return acc;
          }, {})
        );
        setFilterColumnOption(filterOptions);
        setColConfig(ColConfigData, filterOptions);
        fetchTableData();
      },
      error: (error: any) => {
        console.log(error);
        setTableSpinner(false);
      },
    });
  };

  const onDateChange = (date: any) => {
    if (moment(date).format("YYYY-MM-DD") !== moment(filters?.date).format("YYYY-MM-DD")) {
      setFilters({ ...filters, date: moment(date).format("YYYY-MM-DD") });
      fetchTableData({ ...filters, date: moment(date).format("YYYY-MM-DD") });
    }
  };

  const onTableFilters = (appliedFilters: any, sorter: any) => {
    setTableSort({ dataIndex: sorter?.columnKey, sortDirection: sorter?.order });
    setTableFilters({ ...(tableFilters ? tableFilters : {}), ...appliedFilters });
  };

  const setColConfig = (colData: any = tableCOlumnConfig, filterData: any = filterColumnOption, sortData: any = tableSort) => {
    const col: any = sortBy(
      colData
        ?.filter((item: any) => item?.display !== "Y")
        ?.map((item: any) => {
          return {
            ...item,
            title: item?.tableHeaderName,
            ...(filterData?.[item?.dataIndex]
              ? {
                  filters: filterData?.[item?.dataIndex].map((filItem: any) => {
                    return { text: filItem, value: filItem };
                  }),
                  onFilter: (input: any, record: any) => {
                    return record?.[item?.dataIndex] === input;
                  },
                  filteredValue: tableFilters?.[item?.dataIndex] ? tableFilters[item?.dataIndex] : [],
                }
              : {}),
            render: (text: any) => (
              <Tooltip placement="topLeft" title={text}>
                <div style={{ color: `${item?.dataIndex === "current_status" ? (text === "Received" ? "green" : "red") : ""}`, fontWeight: "400" }}>{text}</div>
              </Tooltip>
            ),
            sortOrder: tableSort?.dataIndex === item?.dataIndex ? tableSort?.sortDirection : null,
            sorter: (a: any, b: any) => {
              return a?.[item?.dataIndex] && b?.[item?.dataIndex] ? a?.[item?.dataIndex]?.localeCompare(b?.[item?.dataIndex]) : null;
            },
          };
        }),
      ["order"]
    );
    setTableCOlumnConfig(col);
  };

  useEffect(() => {
    setColConfig();
  }, [tableFilters, tableSort]);

  const getFormattedDate = (current:any) => {
    let showDot = fileAvailableDates?.includes(moment(current).format("YYYY-MM-DD"));
    
    return (
      <div className="ant-picker-cell-inner">
      {showDot && <span className="dateDot"></span>}
      {current.date()}
    </div>
    )
  }

  return (
    <div className="fileTracking-report">
      <div className="fileTracking-report__header">
        <div className="fileTracking-report__header__left">File Tracking Report</div>
        <div className="fileTracking-report__header__right">
          <div className="fileTracking-report__header__date-picker">
            <DatePicker
              value={moment(filters.date)}
              format={"YYYY-MM-DD"}
              onChange={onDateChange}
              allowClear={false}
              dropdownClassName="fileTracking-report__date-selector"
              disabledDate={(currentDate: any) => !fileAvailableDates?.includes(moment(currentDate).format("YYYY-MM-DD"))}
              dateRender={(current) => getFormattedDate(current)}
            />
          </div>

          <div className="fileTracking-report__header__download-icon">
            <CSVLink
              data={tableData}
              headers={tableCOlumnConfig?.map((item: any) => {
                return { label: item?.tableHeaderName, key: item?.dataIndex };
              })}
              onClick={() => {
                console.log("clicked");
              }}
              filename={`File_tracking_${formatDownloadFileNameDate(new Date())}`}
            >
              <Tooltip title="Download XLS" overlayClassName="download-tooltip">
                <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "42px" }} />
              </Tooltip>
            </CSVLink>
          </div>
        </div>
      </div>
      <Table
        scroll={{ y: window.innerHeight - 470 }}
        pagination={{ position: ["bottomRight"] }}
        loading={tableSpinner ? { spinning: tableSpinner, indicator: <HealthemSpinner size={45} /> } : false}
        columns={tableCOlumnConfig}
        dataSource={tableData}
        onChange={(pagination: any, filters: any, sorter: any) => {
          onTableFilters(filters, sorter);
        }}
      />
    </div>
  );
};

export default FileTrackingReport;
